/**
 * MKS-339 — the inbox filter bar.
 *
 * Every control is a controlled select whose value comes from the URL and
 * whose onChange writes back to the URL. The page owns the URL; this component
 * owns none of the state, which is what makes the filters shareable and
 * survive a reload (the acceptance criterion).
 */
import { DropdownSelect } from '../ui/DropdownSelect';
import { Btn } from '../ui/Btn';
import type { HelpdeskFilters as Filters, HelpdeskFilterOptions } from '../../services/api/helpdesk';

export interface HelpdeskFiltersProps {
  filters: Filters;
  options: HelpdeskFilterOptions | undefined;
  onChange: (key: keyof Filters, value: string) => void;
  onReset: () => void;
}

export function HelpdeskFilters({
  filters,
  options,
  onChange,
  onReset,
}: HelpdeskFiltersProps) {
  // Categories belong to projects. With a project selected, offering the other
  // projects' categories would be a filter that can only ever return nothing.
  const categories = (options?.categories ?? []).filter(
    (c) => !filters.projectId || String(c.projectId) === filters.projectId,
  );

  const hasAnyFilter = Object.values(filters).some((v) => v !== undefined && v !== '');

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="helpdesk-filters">
      <DropdownSelect
        aria-label="View"
        value={filters.view ?? 'needs_attention'}
        onChange={(e) => onChange('view', e.target.value)}
      >
        <option value="needs_attention">Needs triage + breaching soon</option>
        <option value="all">All requests</option>
      </DropdownSelect>

      <DropdownSelect
        aria-label="Status"
        value={filters.status ?? ''}
        onChange={(e) => onChange('status', e.target.value)}
      >
        <option value="">Any status</option>
        <option value="triage">In triage</option>
        <option value="open">Open</option>
        <option value="closed">Closed</option>
      </DropdownSelect>

      <DropdownSelect
        aria-label="SLA"
        value={filters.slaState ?? ''}
        onChange={(e) => onChange('slaState', e.target.value)}
      >
        <option value="">Any SLA state</option>
        <option value="breached">Breached</option>
        <option value="warning">Breaching soon</option>
        <option value="ok">On track</option>
        <option value="none">No SLA</option>
      </DropdownSelect>

      <DropdownSelect
        aria-label="Project"
        value={filters.projectId ?? ''}
        onChange={(e) => onChange('projectId', e.target.value)}
      >
        <option value="">Any project</option>
        {(options?.projects ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </DropdownSelect>

      <DropdownSelect
        aria-label="Customer"
        value={filters.customerId ?? ''}
        onChange={(e) => onChange('customerId', e.target.value)}
      >
        <option value="">Any customer</option>
        {(options?.customers ?? []).map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </DropdownSelect>

      <DropdownSelect
        aria-label="Category"
        value={filters.categoryId ?? ''}
        onChange={(e) => onChange('categoryId', e.target.value)}
      >
        <option value="">Any category</option>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </DropdownSelect>

      <DropdownSelect
        aria-label="Owner"
        value={filters.owner ?? ''}
        onChange={(e) => onChange('owner', e.target.value)}
      >
        <option value="">Any owner</option>
        <option value="unassigned">Unassigned</option>
        {(options?.owners ?? []).map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </DropdownSelect>

      {hasAnyFilter && (
        <Btn variant="ghost" size="sm" onClick={onReset}>
          Reset
        </Btn>
      )}
    </div>
  );
}
